import React, { useEffect, useId, useRef, useState } from 'react';

export interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

function getSelectedLabelValue(options: SelectOption[], value: string, pinnedOption?: SelectOption): string {
    return options.find((o) => o.value === value)?.label ??
        (pinnedOption?.value === value ? pinnedOption.label : '') ?? '';
}

function filterSearchableOptions(options: SelectOption[], search: string): SelectOption[] {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter((o) =>
        o.label.toLowerCase().includes(lower) || o.value.toLowerCase().includes(lower)
    );
}

function calcShowPinned(pinnedOption: SelectOption | undefined, search: string): boolean {
    if (!pinnedOption) return false;
    if (!search) return true;
    const lower = search.toLowerCase();
    return pinnedOption.label.toLowerCase().includes(lower) ||
        pinnedOption.value.toLowerCase().includes(lower);
}

interface SearchableSelectProps {
    options: SelectOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    required?: boolean;
    invalid?: boolean;
    pinnedOption?: SelectOption;
    id?: string;
    onSearchChange?: (term: string) => void;
    onLoadMore?: () => void;
    loadingMore?: boolean;
    hasMore?: boolean;
    searching?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Select...',
    disabled = false,
    loading = false,
    required = false,
    invalid = false,
    pinnedOption,
    id,
    onSearchChange,
    onLoadMore,
    loadingMore = false,
    hasMore = false,
    searching = false,
}) => {
    const triggerId = useId();
    const listboxId = useId();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    // remembers the label of the current value so it keeps showing even if a later
    // search/pagination refresh replaces `options` with a page that no longer contains it
    const [resolvedLabel, setResolvedLabel] = useState<SelectOption | null>(null);

    useEffect(() => {
        if (!value) {
            setResolvedLabel(null);
            return;
        }
        const match = options.find((o) => o.value === value) ??
            (pinnedOption?.value === value ? pinnedOption : undefined);
        if (match) setResolvedLabel(match);
    }, [options, value, pinnedOption]);

    const selectedLabel = getSelectedLabelValue(options, value, pinnedOption) ||
        (resolvedLabel?.value === value ? resolvedLabel.label : '');
    const filtered = onSearchChange ? options : filterSearchableOptions(options, search);
    const showPinned: boolean = calcShowPinned(pinnedOption, search);
    const hasResults: boolean = showPinned || filtered.length > 0;

    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    useEffect(() => {
        if (open && searchRef.current) {
            searchRef.current.focus();
        } else if (!open) {
            setSearch('');
            onSearchChange?.('');
        }
    }, [open, onSearchChange]);

    useEffect(() => {
        const list = listRef.current;
        if (!list || !open || !onLoadMore) return;
        const handleScroll = () => {
            if (list.scrollTop + list.clientHeight >= list.scrollHeight - 40 && hasMore && !loadingMore && !searching) {
                onLoadMore();
            }
        };
        list.addEventListener('scroll', handleScroll);
        return () => list.removeEventListener('scroll', handleScroll);
    }, [open, hasMore, loadingMore, searching, onLoadMore]);

    const handleToggle = () => {
        if (disabled || loading) return;
        setOpen((prev) => {
            if (prev) {
                setSearch('');
                onSearchChange?.('');
            }
            return !prev;
        });
    };

    const handleTriggerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        const target = e.target as HTMLElement;
        if (target.closest('.searchable-select__clear')) {
            e.stopPropagation();
            onChange('');
            setOpen(true);
            setSearch('');
            return;
        }
        handleToggle();
    };

    const handleSelect = (optionValue: string, isDisabled?: boolean) => {
        if (isDisabled) return
        onChange(optionValue);
        setOpen(false);
        setSearch('');
    };

    const triggerLabel = loading ? 'Loading...' : selectedLabel || placeholder;

    return (
        <div
            className={`searchable-select${open ? ' is-open' : ''}${disabled || loading ? ' is-disabled' : ''}`}
            ref={containerRef}
        >
            {/* Hidden native input for form required validation */}
            <input
                type="text"
                id={id}
                value={value}
                required={required}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                className="searchable-select__hidden-input"
            />

            <button
                id={triggerId}
                type="button"
                className={`searchable-select__trigger form-control${invalid ? ' is-invalid' : ''}`}
                onClick={handleTriggerClick}
                disabled={disabled || loading}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-label={selectedLabel || placeholder}
            >
                <span className={`searchable-select__value${!value ? ' is-placeholder' : ''}`}>
                    {triggerLabel}
                </span>
                {value && !loading && !disabled && (
                    <span
                        className="searchable-select__clear"
                        aria-hidden="true"
                    >
                        &times;
                    </span>
                )}
                {loading ? (
                    <span className="searchable-select__spinner" aria-hidden="true" />
                ) : (
                    <span className={`searchable-select__chevron${open ? ' is-open' : ''}`} aria-hidden="true" />
                )}
            </button>

            {open && (
                <div id={listboxId} className="searchable-select__panel" aria-labelledby={triggerId}>
                    <div className="searchable-select__search-wrap">
                        <input
                            ref={searchRef}
                            type="text"
                            className="searchable-select__search"
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                onSearchChange?.(e.target.value);
                            }}
                            aria-label="Search options"
                        />
                    </div>
                    <ul className="searchable-select__list" ref={listRef}>
                        {!hasResults && (
                            <li className="searchable-select__no-results">No option</li>
                        )}
                        {hasResults && showPinned && pinnedOption && (
                            <li>
                                <button
                                    type="button"
                                    className={`searchable-select__option${pinnedOption.value === value ? ' is-selected' : ''}`}
                                    aria-pressed={pinnedOption.value === value}
                                    onClick={() => handleSelect(pinnedOption.value)}
                                >
                                    {pinnedOption.label}
                                </button>
                            </li>
                        )}
                        {hasResults && filtered.map((o) => (
                            <li key={o.value}>
                                <button
                                    type="button"
                                    className={`searchable-select__option${o.value === value ? ' is-selected' : ''}`}
                                    aria-pressed={o.value === value}
                                    aria-disabled={o.disabled}
                                    disabled={o.disabled}
                                    onClick={() => handleSelect(o.value, o.disabled)}
                                >
                                    {o.label}
                                </button>
                            </li>
                        ))}
                        {loadingMore && (
                            <li className="searchable-select__loading-more" aria-hidden="true">Loading...</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;